import { useParams } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import MessageList from '../components/chat/MessageList';
import ChatInput from '../components/chat/ChatInput';
import { motion } from 'framer-motion';
import {
  getChatHistory,
  getChatSources,
  getDeepSearchResult,
  getDeepSearchStatus,
  getMemorySummary,
  sendMessageToBot,
  startChatSession,
  startDeepSearch,
  streamSimpleMessage,
} from '../api/api';

import { toast } from 'react-hot-toast';
import { useSelector, useDispatch } from 'react-redux';
import { storeContractReport } from '../redux/slices/reportSlice';
import { useNavigate } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { fetchChatSessions } from '../redux/slices/chatSessionsSlice';
import { fetchUserStatistics } from '../redux/slices/userStatisticsSlice';
import ScaleLogo from '.././assets/scale-balance.svg';
import { createController } from '../redux/slices/requestSlice';
import { store } from '../redux/store';
import { handleApiError } from '../helpers/handleApiError';
import ChatNotFound from './ChatNotFound';
import PlanLimitReachedModal from './PlanLimitReachedModal';
import UpgradeModal from '../components/usage/UpgradeModal';

export default function ChatView({
  allMessages,
  setAllMessages,
  isDarkMode,
  fetchDocumentDetails,
  setChatHistory,
  setResources,
  resources,
}) {
  const { chatId } = useParams();
  const activeId = chatId || 'current';

  // inside ChatView
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isStreaming = useSelector((state) => state.streaming.enabled);

  const messages = allMessages[activeId] || [];
  const [hasSentFirstMessage, setHasSentFirstMessage] = useState(false);
  const [loading, setLoading] = useState(false);
  const [chatNotFound, setChatNotFound] = useState(false);
  const [memoryInfo, setMemoryInfo] = useState(null);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isResearchMode, setIsResearchMode] = useState(false);
  const [isStreamingResponse, setIsStreamingResponse] = useState(false);
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const stats = useSelector((state) => state.userStatistics.stats);
  const user = useSelector((state) => state.auth.user);

  // const activeOrder = user?.Orders?.find(
  //   (order) =>
  //     order.subscription_status === 'active' ||
  //     order.subscription_status === 'paused'
  // );

  const activeOrder = user?.subscription || null;

  const remainingChatQueries =
    stats?.usage_month_to_date?.chatbot_queries?.value;
  const remainingResearchQueries =
    stats?.usage_month_to_date?.professional_research?.value;

  const [researchStep, setResearchStep] = useState(0);
  const uid = user?.id;
  const location = useLocation();
  const isJustCreated = location.state?.justCreated === true;
  const isResearch = location.pathname.includes('/research/');
  const isFirstTimeResearch = location.state?.isFirstTimeResearch === true;

  useEffect(() => {
    if (!isResearch) return;

    if (isFirstTimeResearch) return;

    navigate('/app/chat', { replace: true });
  }, [isResearch, isFirstTimeResearch, navigate]);

  useEffect(() => {
    const init = async () => {
      setChatNotFound(false);
      // ⛔ HARD EXIT: if chat is newly created, skip history & memory entirely
      if (isJustCreated) return;
      if (location.state?.isResearchSession) {
        setLoading(false);
        return;
      }

      if (!chatId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        // 2. chat history
        const historyData = await getChatHistory({
          uid,
          chat_session_id: chatId,
          limit: 100,
          offset: 0,
        });
        // 1. memory summary
        await updateMemoryInfo(chatId);
        setResources(historyData?.research_sources);

        const msgs = historyData.messages.map((m, idx) => ({
          id: idx + 1,
          role: m.role,
          content: m.content,
          timestamp: m.timestamp,
          ...(m.role === 'ai' && m.memory_id ? { memory_id: m.memory_id } : {}),
        }));

        setAllMessages((prev) => ({ ...prev, [chatId]: msgs }));

        const firstUserMessage = historyData.messages.find(
          (m) => m.role === 'user',
        );

        if (firstUserMessage) {
          const space = historyData?.spaces?.[0];

          const sessionMeta = {
            id: historyData?.chat_session_id,
            title: historyData?.chat_session?.title,
            content:
              firstUserMessage.content.slice(0, 30) || 'Untitled Conversation',
            date: new Date(firstUserMessage.timestamp).toLocaleDateString(),
            space: space
              ? {
                  id: space.space_id,
                  name: space.name,
                  description: space.description,
                }
              : null,
          };

          setChatHistory((prev) => {
            const idx = prev.findIndex((c) => c.id === sessionMeta.id);
            if (idx !== -1) {
              const updated = [...prev];
              updated[idx] = { ...updated[idx], ...sessionMeta };
              return updated;
            }
            return [...prev, sessionMeta];
          });
        }
      } catch (err) {
        if (err?.response?.status === 404) {
          setChatNotFound(true);
          setAllMessages((prev) => ({ ...prev, [chatId]: [] }));
          return;
        }

        handleApiError(err, '⚠️ Could not load chat');
      } finally {
        setLoading(false);
      }
    };

    init();
  }, [chatId, isJustCreated]);

  useEffect(() => {
    if (isFirstTimeResearch || isJustCreated) {
      window.history.replaceState({}, document.title, location.pathname);
    }
  }, [isFirstTimeResearch, isJustCreated, location.pathname]);
  useEffect(() => {
    return () => {
      const controller = store.getState().request.controller;
      controller?.abort();
      console.log('🛑 Aborted due to route change / unmount');
    };
  }, []);

  const messagesEndRef = useRef(null);
  const stopPollingRef = useRef(false);

  const scrollToBottom = (delay = 30) => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'end',
      });
    }, delay);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isStreamingResponse || isLoading) return;

    if (!input.trim()) return;

    // ✅ INTENT-BASED creation
    dispatch(createController());
    const controller = store.getState().request.controller;

    console.log('🟢 Controller created on send', {
      aborted: controller.signal.aborted,
      time: Date.now(),
    });

    // 🚫 1. Check limits BEFORE doing anything else
    // 🚫 GLOBAL BLOCK — applies to EVERYTHING
    if (activeOrder?.status === 'paused') {
      toast.error(
        'Your subscription is paused. Please resume your subscription to continue.',
      );
      return;
    }

    // 🚫 MODE-SPECIFIC LIMITS
    if (isResearchMode) {
      if (remainingResearchQueries === 0) {
        setShowLimitModal(true);
        return;
      }
    } else {
      if (remainingChatQueries === 0) {
        setShowLimitModal(true);
        return;
      }
    }

    const userMessage = {
      id: Date.now(),
      role: 'user',
      content: input,
      timestamp: new Date().toISOString(),
    };
    const updatedMessages = [...messages, userMessage];
    setAllMessages((prev) => ({
      ...prev,
      [activeId]: updatedMessages,
    }));
    scrollToBottom(100);

    setInput('');
    if (!isStreaming) {
      setIsLoading(true);
    }
    if (!hasSentFirstMessage) setHasSentFirstMessage(true);

    try {
      if (isResearchMode) {
        const { research_id } = await startDeepSearch(uid, userMessage.content);
        navigate(`/app/research/${research_id}`, {
          replace: false,
          state: {
            isResearchSession: true,
            isFirstTimeResearch: true,
          },
        });

        // ⭐ ADD THE USER MESSAGE TO THE NEW RESEARCH SESSION
        setAllMessages((prev) => ({
          ...prev,
          [research_id]: [
            {
              id: Date.now(),
              role: 'user',
              content: userMessage.content,
              timestamp: new Date().toISOString(),
            },
          ],
        }));
        let attempts = 0;
        const maxAttempts = 120;
        let currentStatus = 'pending';
        let statusResponse = null;

        while (attempts < maxAttempts && !stopPollingRef.current) {
          await new Promise((resolve) => setTimeout(resolve, 5000));

          if (stopPollingRef.current) break;

          try {
            statusResponse = await getDeepSearchStatus(research_id);
            currentStatus = statusResponse.status;

            if (
              statusResponse.current_step === 'Queued for legal research' ||
              statusResponse.current_step === 'Initializing legal research'
            ) {
              setResearchStep(1);
            } else if (statusResponse.current_step === 'Compiling results') {
              setResearchStep(2);
            } else if (
              statusResponse.current_step === 'Legal research complete'
            ) {
              setResearchStep(3);
            }

            console.log(
              `[Poll ${attempts + 1}] Status: ${currentStatus}, Step: ${
                statusResponse.current_step
              }, Progress: ${statusResponse.progress}%`,
            );

            if (currentStatus === 'completed') {
              console.log('✅ Research completed successfully');
              break;
            }

            if (currentStatus === 'error') {
              throw new Error(
                statusResponse.error_message ||
                  'Research failed. Please try again.',
              );
            }

            attempts++;
          } catch (pollError) {
            console.error('Polling error:', pollError);

            if (pollError?.response?.status !== 404) {
              attempts++;
              if (attempts >= 10) {
                throw new Error(
                  'Unable to check research status. Please try again later.',
                );
              }
              continue;
            }

            throw pollError;
          }
        }

        if (currentStatus !== 'completed' && attempts >= maxAttempts) {
          throw new Error(
            'Research is taking longer than expected. Please check back later or contact support.',
          );
        }

        if (currentStatus === 'completed') {
          const result = await getDeepSearchResult(research_id);

          if (!result || !result.research_report) {
            throw new Error('Research completed but no report was generated.');
          }

          const systemMessage = {
            id: Date.now() + 1,
            role: 'system',
            content: result.research_report,
            source: 'Deep Research',
            timestamp: new Date().toISOString(),
          };

          dispatch(
            storeContractReport({
              contract_name: `DeepResearch_${new Date().toISOString()}`,
              analysis_date: new Date().toISOString(),
              analysis_type: 'research',
              analysis_results: {
                research: result.research_report,
              },
            }),
          );

          dispatch(fetchUserStatistics(uid));

          setAllMessages((prev) => ({
            ...prev,
            [research_id]: [...(prev[research_id] || []), systemMessage],
          }));

        }

        return;
      }

      let sessionId = chatId;

      if (!sessionId) {
        const session = await startChatSession({
          uid,
          title: userMessage.content.slice(0, 30) || `Chat_${Date.now()}`,
        });
        sessionId = session.chat_session_id;

        setChatHistory((prev) => [
          ...prev,
          {
            id: session.chat_session_id,
            title: session.title || userMessage.content.slice(0, 30),
            content: userMessage.content.slice(0, 30),
            date: new Date().toLocaleDateString(),
          },
        ]);

        navigate(`/app/chat/${sessionId}`, {
          replace: true,
          state: { justCreated: true },
        });
      }

      const tempId = Date.now() + 1;
      setAllMessages((prev) => ({
        ...prev,
        [sessionId]: [
          ...updatedMessages,
          {
            id: tempId,
            role: 'assistant', // 🔥 FIX ROLE
            content: '',
            timestamp: new Date().toISOString(), // 🔥 ADD TIME
          },
        ],
      }));

      if (isStreaming) {
        setIsStreamingResponse(true);

        let buffer = '';
        let doneStreaming = false;
        let started = false;

        const streamChunk = () => {
          if (controller?.signal.aborted) {
            console.log('⛔ chunk loop stopped');
            return;
          }
          if (buffer.length === 0) {
            if (doneStreaming) {
              updateMemoryInfo(sessionId);
              return;
            }
            setTimeout(streamChunk, 22);
            return;
          }

          const nextChunk = buffer.slice(0, 3);
          buffer = buffer.slice(3);

          setAllMessages((prev) => {
            const updated = { ...prev };
            updated[sessionId] = updated[sessionId].map((msg) =>
              msg.id === tempId
                ? { ...msg, content: msg.content + nextChunk }
                : msg,
            );
            return updated;
          });

          setTimeout(streamChunk, 30);
        };

        streamSimpleMessage({
          uid,
          message: userMessage.content,
          signal: controller?.signal,
          chat_session_id: sessionId,
          onData: (chunk) => {
            buffer += chunk;
            if (!started) {
              started = true;
              streamChunk();
            }
          },
          onDone: async () => {
            doneStreaming = true;

            try {
              const sourcesData = await getChatSources(uid, sessionId);
              setResources(sourcesData?.sources || []);
            } catch (err) {
              console.error('Sources fetch failed:', err);

              toast.error(
                err?.response?.data?.error ||
                  err?.response?.data?.message ||
                  'Failed to load sources for this response.',
              );
            }

            dispatch(fetchChatSessions());
            dispatch(fetchUserStatistics(uid));
            setIsStreamingResponse(false);

            scrollToBottom(100);
          },
          onError: (err) => {
            buffer = ''; // ⛔ kill leftover chunks immediately
            doneStreaming = true; // ⛔ force loop to end cleanly
            setIsStreamingResponse(false);

            const message =
              err?.response?.data?.error ||
              err?.message ||
              '⚠️ Streaming failed. Please try again.';

            // toast.error(message);

            setAllMessages((prev) => ({
              ...prev,
              [sessionId]: [
                ...(prev[sessionId] || []),
                {
                  id: Date.now(),
                  role: 'system',
                  content: message,
                  timestamp: new Date().toISOString(),
                },
              ],
            }));
          },
        });
      } else {
        const botResponse = await sendMessageToBot(
          uid,
          userMessage.content,
          sessionId,
          controller?.signal,
        );
        if (!botResponse) {
          setIsLoading(false);
          return;
        }
        updateMemoryInfo(sessionId);

        setResources(botResponse?.research_sources);

        setAllMessages((prev) => ({
          ...prev,
          [sessionId]: updatedMessages.concat({
            id: tempId,
            role: 'system',
            content: botResponse?.response || 'No response received.',
            timestamp: new Date().toISOString(),
          }),
        }));
        scrollToBottom(50);

        dispatch(fetchChatSessions());
        dispatch(fetchUserStatistics(uid));

        setIsLoading(false);
      }
    } catch (error) {
      console.error('ChatView error:', error);

      handleApiError(
        error,
        'Something went wrong while sending your message. Please try again.',
      );

      // const errorMessage = {
      //   id: Date.now() + 2,
      //   role: 'system',
      //   content: messageText,
      // };
      // setAllMessages((prev) => ({
      //   ...prev,
      //   [activeId]: [...updatedMessages, errorMessage],
      // }));
    } finally {
      setIsLoading(false);
      setIsResearchMode(false);
      setResearchStep(0);
    }
  };

  const updateMemoryInfo = async (chat_session_id) => {
    try {
      const memoryData = await getMemorySummary(uid, chat_session_id);

      setMemoryInfo(memoryData.memory_summary);
    } catch (error) {
      handleApiError(error, 'Failed to get memory info');
    }
  };
  useEffect(() => {
    return () => {
      stopPollingRef.current = true;
    };
  }, []);

  const prevChatIdRef = useRef(chatId);

  useEffect(() => {
    const prevChatId = prevChatIdRef.current;

    // 🟢 First-time chat → preserve everything
    if (isJustCreated) {
      prevChatIdRef.current = chatId;
      return;
    }

    // 🟢 First-time research → preserve everything
    if (isFirstTimeResearch) {
      prevChatIdRef.current = chatId;
      return;
    }

    // 🔴 Chat deleted → FULL RESET
    if (prevChatId && !chatId) {
      setAllMessages((prev) => ({ ...prev, current: [] }));
      setInput('');
      setHasSentFirstMessage(false);
      setIsStreamingResponse(false);
      setIsResearchMode(false);
      setResearchStep(0);
      setIsLoading(false);
      setLoading(false);
      setResources(null);
    }

    // 🟡 Switched between existing chats → UI RESET ONLY
    if (prevChatId && chatId && prevChatId !== chatId) {
      stopPollingRef.current = false;
      setInput('');
      setHasSentFirstMessage(false);
      setIsStreamingResponse(false);
      setIsResearchMode(false);
      setResearchStep(0);
      setIsLoading(false);
    }

    prevChatIdRef.current = chatId;
  }, [chatId, isJustCreated, isFirstTimeResearch]);

  useEffect(() => {
    if (loading) return;
    if (isStreamingResponse) return;

    scrollToBottom(30);
  }, [allMessages[activeId]?.length, isStreamingResponse, loading]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (!isStreamingResponse && !isLoading) {
        // 🚫 only allow when not streaming
        handleSubmit(e);
      }
    }
  };
  const isEmpty =
    (!chatId || chatId === 'current') && messages.length === 0 && !loading;
  if (chatNotFound) {
    return (
      <ChatNotFound
        isDarkMode={isDarkMode}
        onStartNewChat={() => navigate('/app/chat', { replace: true })}
        onGoBack={() => navigate(-1)}
      />
    );
  }
  return (
    <div className="relative flex flex-col w-full overflow-visible">
      {/* TOP AREA (Empty Hero OR Messages) */}
      {isEmpty ? (
        <div className="flex flex-col items-center px-4 pt-24 text-center">
          {/* HERO */}
          <div className="flex flex-col items-center justify-center">
            <div className="p-2.5 rounded-full mb-4 bg-[#E3E5FC] flex items-center justify-center">
              <img src={ScaleLogo} alt="Scale Icon" className="h-8 w-8" />
            </div>

            <h3
              className={`text-2xl font-bold leading-tight ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }`}
            >
              Legal AI Chatbot & <br /> Professional Research
            </h3>

            <p
              className={`mt-3 text-sm max-w-md mx-auto ${
                isDarkMode ? 'text-gray-300' : 'text-gray-600'
              }`}
            >
              Ask any legal question to get started. <br />
              Toggle Professional Research mode for more in-depth analysis.
            </p>
          </div>
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto super-ultra-thin px-1">
          <MessageList
            messages={messages}
            isLoading={isLoading}
            isResearchMode={isResearchMode}
            researchStep={researchStep}
            isStreamingResponse={isStreamingResponse}
            messagesEndRef={messagesEndRef}
            isDarkMode={isDarkMode}
            chatSessionId={chatId || ''}
            fetchDocumentDetails={fetchDocumentDetails}
            loading={loading}
            resources={resources}
          />
        </div>
      )}

      {/* ONE AND ONLY ONE CHAT INPUT BELOW */}
      {isResearch && hasSentFirstMessage ? null : loading ? (
        <div className="w-full px-4 py-3 border-t flex items-center justify-center">
          <div className="animate-spin h-6 w-6 border-2 border-brand border-t-transparent rounded-full"></div>
          <span className="ml-2 text-sm text-gray-500">Preparing chat...</span>
        </div>
      ) : (
        <motion.div
          className="w-full px-4 z-10"
          layout
          initial={{ y: -120, opacity: 0 }} // lifted visually
          animate={{
            y: hasSentFirstMessage ? 0 : 0,
            opacity: 1,
          }}
          transition={{ type: 'spring', stiffness: 120, damping: 20 }}
        >
          <ChatInput
            input={input}
            setInput={setInput}
            handleSubmit={handleSubmit}
            handleKeyDown={handleKeyDown}
            isDarkMode={isDarkMode}
            isResearchActive={isResearchMode}
            setIsResearchActive={setIsResearchMode}
            isLoading={isLoading}
            isStreaming={isStreaming}
            isStreamingResponse={isStreamingResponse}
          />
        </motion.div>
      )}

      <PlanLimitReachedModal
        open={showLimitModal}
        onClose={() => setShowLimitModal(false)}
        activeOrder={activeOrder}
        onUpgrade={() => {
          setShowLimitModal(false);
          setShowUpgradeModal(true);
        }}
      />

      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        isDarkMode={isDarkMode}
      />
    </div>
  );
}
